(() => {
  if (window.__technoProductSystemInitialized) return;
  window.__technoProductSystemInitialized = true;

  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const clickGalleryTarget = (stage, target) => {
    if (!target) return false;
    const productInfo = stage.closest('product-info');
    const thumb = productInfo?.querySelector(`[data-target="${CSS.escape(target)}"] button`)
      || document.querySelector(`[data-target="${CSS.escape(target)}"] button`);
    if (!thumb) return false;
    thumb.click();
    return true;
  };

  const setPairState = (stage, state, { user = false } = {}) => {
    if (!stage || !['off', 'on'].includes(state)) return;
    stage.dataset.state = state;
    if (user) stage.dataset.userSelected = 'true';

    const toggle = stage.querySelector('[data-techno-pair-toggle]');
    const status = stage.querySelector('[data-techno-pair-status]');
    const visibleLabel = stage.parentElement?.querySelector('[data-techno-pair-visible-label]');
    const isOn = state === 'on';
    if (toggle) {
      toggle.setAttribute('aria-pressed', String(isOn));
      toggle.setAttribute('aria-label', isOn ? 'Switch product lights off' : 'Switch product lights on');
    }
    if (status) status.textContent = isOn ? 'Product lights on' : 'Product lights off';
    if (visibleLabel) visibleLabel.textContent = isOn ? 'Lights on' : 'Lights off';
    stage.dispatchEvent(new CustomEvent('techno:productillumination', { bubbles: true, detail: { state } }));
  };

  const desiredInitialState = (stage, mode) => {
    const preference = stage.dataset.initialState || 'follow';
    if (preference === 'on' || preference === 'off') return preference;
    return mode === 'dark' ? 'on' : 'off';
  };

  const syncMode = (mode, force = false) => {
    document.querySelectorAll('[data-techno-paired-stage]').forEach((stage) => {
      if (!force && stage.dataset.userSelected === 'true') return;
      if (stage.dataset.initialState !== 'follow' && !force) return;
      setPairState(stage, desiredInitialState(stage, mode));
    });
  };

  const initPairStage = (stage) => {
    if (!stage || stage.dataset.technoInitialized === 'true') return;
    stage.dataset.technoInitialized = 'true';
    setPairState(stage, desiredInitialState(stage, root.dataset.colorMode || 'light'));

    const onImage = stage.querySelector('.techno-paired-stage__image--on');
    if (onImage && onImage.decode) onImage.decode().catch(() => {});
  };

  const setupTabs = (section) => {
    if (!section || section.dataset.technoTabsInitialized === 'true') return;
    section.dataset.technoTabsInitialized = 'true';
    const tabs = [...section.querySelectorAll('[role="tab"]')];
    const panels = [...section.querySelectorAll('[role="tabpanel"]')];

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        tabs.forEach((item) => item.setAttribute('aria-selected', String(item === tab)));
        panels.forEach((panel) => { panel.hidden = panel.id !== tab.getAttribute('aria-controls'); });
      });
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let index = tabs.indexOf(tab);
        if (event.key === 'ArrowRight') index = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') index = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') index = 0;
        if (event.key === 'End') index = tabs.length - 1;
        tabs[index].focus();
        tabs[index].click();
      });
    });

    section.querySelectorAll('.techno-product-tabs__mobile-trigger').forEach((button, index) => {
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        section.querySelectorAll('.techno-product-tabs__mobile-trigger').forEach((item) => item.setAttribute('aria-expanded', 'false'));
        button.setAttribute('aria-expanded', String(!expanded));
        if (!expanded) window.setTimeout(() => button.scrollIntoView({ block: 'nearest', behavior: reduceMotion.matches ? 'auto' : 'smooth' }), 40);
      });
      if (index === 0) button.setAttribute('aria-expanded', 'true');
    });
  };

  const setupSticky = () => {
    const sticky = document.querySelector('[data-techno-product-sticky]');
    const mainButton = document.querySelector('.techno-product-main .product-form__submit');
    if (!sticky || !mainButton || !('IntersectionObserver' in window) || sticky.dataset.technoInitialized === 'true') return;
    sticky.dataset.technoInitialized = 'true';
    const observer = new IntersectionObserver(([entry]) => sticky.classList.toggle('is-visible', !entry.isIntersecting), { threshold: .05 });
    observer.observe(mainButton);
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll?.('[data-techno-paired-stage]').forEach(initPairStage);
    scope.querySelectorAll?.('[data-techno-product-tabs]').forEach(setupTabs);
    setupSticky();
  };

  document.addEventListener('click', async (event) => {
    const pairToggle = event.target.closest('[data-techno-pair-toggle]');
    if (pairToggle) {
      event.preventDefault();
      const stage = pairToggle.closest('[data-techno-paired-stage]');
      if (stage) setPairState(stage, stage.dataset.state === 'on' ? 'off' : 'on', { user: true });
      return;
    }

    const sceneButton = event.target.closest('[data-techno-pair-scene]');
    if (sceneButton) {
      event.preventDefault();
      const stage = sceneButton.closest('.techno-product-pair-slide')?.querySelector('[data-techno-paired-stage]')
        || sceneButton.parentElement?.previousElementSibling;
      const target = sceneButton.dataset.sceneTarget || stage?.dataset.sceneTarget;
      if (stage) clickGalleryTarget(stage, target);
      return;
    }

    const copyButton = event.target.closest('[data-techno-copy]');
    if (copyButton) {
      const value = copyButton.dataset.technoCopy || '';
      try {
        await navigator.clipboard.writeText(value);
        const text = copyButton.querySelector('[data-techno-copy-text]');
        if (text) text.textContent = copyButton.dataset.copiedLabel || 'Copied';
        window.setTimeout(() => { if (text) text.textContent = copyButton.dataset.copyLabel || 'Copy'; }, 1600);
      } catch (error) {
        // The visible identifier remains selectable if clipboard access is blocked.
      }
      return;
    }

    const stickyButton = event.target.closest('[data-techno-sticky-atc]');
    if (stickyButton) {
      const mainButton = document.querySelector('.techno-product-main .product-form__submit:not([disabled])');
      mainButton?.click();
    }
  });

  document.addEventListener('techno:modechange', (event) => {
    syncMode(event.detail?.mode || root.dataset.colorMode || 'light');
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
})();
